import Cookies from 'js-cookie'
import type { User } from '../../types/auth'
import authConfig from '../../config/authConfig'
import { logger } from '../../utils/logger'
import router from '../../router'

// Constants from config
const AUTH_COOKIE_NAME = authConfig.cookie.name
const COOKIE_EXPIRATION_DAYS = authConfig.cookie.expirationDays
const ALLOWED_DOMAIN = authConfig.allowedDomain
const DEFAULT_AVATAR = '/assets/images/default-avatar.svg'

// Check if the email domain is allowed
const isAllowedDomain = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    logger.error('Invalid email provided to isAllowedDomain:', email)
    return false
  }

  const emailParts = email.split('@')
  if (emailParts.length !== 2) {
    logger.error('Invalid email format:', email)
    return false
  }

  const domain = emailParts[1]
  return domain === ALLOWED_DOMAIN
}

// Save user to cookie with expiration
const saveUserToCookie = (user: User): void => {
  Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(user), {
    expires: COOKIE_EXPIRATION_DAYS,
    secure: window.location.protocol === 'https:', // Only send over HTTPS
    sameSite: 'strict', // Prevent CSRF attacks
    path: '/' // Restrict to this path
  })
}

// Get user from cookie
const getUserFromCookie = (): User | null => {
  const userStr = Cookies.get(AUTH_COOKIE_NAME)
  if (!userStr) return null

  try {
    return JSON.parse(userStr)
  } catch (e) {
    return null
  }
}

// Remove user from cookie
const removeUserFromCookie = (): void => {
  Cookies.remove(AUTH_COOKIE_NAME)
}

// Handle successful Authentik login
const handleAuthentikLoginSuccess = (user: User): User => {
  if (!isAllowedDomain(user.email)) {
    throw new Error('Access restricted to authorized email addresses only.')
  }

  saveUserToCookie(user)
  return user
}

// Get user from cookie
const getUser = (): User | null => {
  return getUserFromCookie()
}

// Check if user is authenticated
const isAuthenticated = (): boolean => {
  return !!getUserFromCookie()
}

// Logout user
const logout = (): void => {
  // Check if we have Authentik tokens using the secure storage
  // Import the storage check from authentikAuth
  import('./authentikAuth')
    .then(({ isAuthentikAuthenticated, signOutAuthentik }) => {
      if (isAuthentikAuthenticated()) {
        // Handle Authentik logout
        signOutAuthentik().catch(logger.error)
      } else {
        removeUserFromCookie()
        router.push('/login')
      }
    })
    .catch(() => {
      // If import fails, just do local logout
      removeUserFromCookie()
      router.push('/login')
    })
}

export default {
  handleAuthentikLoginSuccess,
  getUser,
  isAuthenticated,
  logout,
  isAllowedDomain
}
